import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { MfaEncourageBanner } from "../components/MfaSettingsCard";
import StrengthsDashboardCard from "../components/StrengthsDashboardCard";
import LiveClassWidget from "../components/LiveClassWidget";
import { 
  BookOpen, 
  Users, 
  GraduationCap, 
  ArrowRight,
  FileText,
  Trophy,
  Target,
  ClipboardList,
  CheckCircle,
  Clock,
  Pencil,
  Save,
  X,
  Briefcase,
  Mail,
  MapPin,
  Award,
  Building2,
  CalendarDays,
  Rocket,
  FileSignature
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const SignDocsBanner = () => {
  const [pending, setPending] = useState(0);
  useEffect(() => {
    fetch(`${API}/signature-requests/mine`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setPending((d.requests || []).filter(r => r.status === "pending").length))
      .catch(() => {});
  }, []);
  if (!pending) return null;
  return (
    <Link to="/sign" data-testid="sign-docs-banner"
      className="flex items-center justify-between gap-3 rounded-xl border border-brand-gold/40 bg-brand-gold/10 px-5 py-3 hover:bg-brand-gold/20 transition">
      <span className="flex items-center gap-3 text-slate-800">
        <FileSignature className="w-5 h-5 text-brand-navy" />
        <span className="font-medium">You have {pending} document{pending > 1 ? "s" : ""} to review &amp; sign</span>
      </span>
      <span className="text-brand-navy font-semibold text-sm flex items-center gap-1">Review &amp; Sign <ArrowRight className="w-4 h-4" /></span>
    </Link>
  );
};

const Dashboard = () => {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [currentEnrollment, setCurrentEnrollment] = useState(null);
  const [talentProfile, setTalentProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  // Post-training status
  const [editingStatus, setEditingStatus] = useState(false);
  const [statusForm, setStatusForm] = useState({
    post_training_status: "",
    employer_name: "",
    start_date: "",
    notes: "",
    industry: ""
  });
  const [savingStatus, setSavingStatus] = useState(false);

  const POST_TRAINING_STATUSES = [
    "Internship", "Apprenticeship", "On the Job Training",
    "Employment", "Seeking Employment", "Ongoing Education"
  ];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, enrollRes, currentEnrollRes, profileRes] = await Promise.all([
          fetch(`${API}/stats/participant`, { credentials: 'include' }),
          fetch(`${API}/enrollments/me`, { credentials: 'include' }),
          fetch(`${API}/enrollment/current`, { credentials: 'include' }),
          fetch(`${API}/talent/me`, { credentials: 'include' })
        ]);

        if (statsRes.ok) setStats(await statsRes.json());
        if (enrollRes.ok) setEnrollments(await enrollRes.json());
        if (currentEnrollRes.ok) {
          const data = await currentEnrollRes.json();
          setCurrentEnrollment(data);
        }
        if (profileRes.ok) {
          const data = await profileRes.json();
          if (data) {
            setTalentProfile(data);
            setProfileForm(data);
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Initialize post-training status from user data
  useEffect(() => {
    if (user) {
      setStatusForm({
        post_training_status: user.post_training_status || "",
        employer_name: user.post_training_employer || "",
        start_date: user.post_training_start_date || "",
        notes: user.post_training_notes || "",
        industry: user.industry || ""
      });
    }
  }, [user]);

  const savePostTrainingStatus = async () => {
    if (!statusForm.post_training_status) {
      toast.error("Please select a status");
      return;
    }
    setSavingStatus(true);
    try {
      // Save post-training status
      const res = await fetch(`${API}/user/post-training-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(statusForm)
      });
      if (res.ok) {
        let updated = await res.json();
        // Also save industry if changed
        if (statusForm.industry) {
          const indRes = await fetch(`${API}/user/industry`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ industry: statusForm.industry })
          });
          if (indRes.ok) updated = await indRes.json();
        }
        if (setUser) setUser(updated);
        toast.success("Post-training status updated!");
        setEditingStatus(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update status");
      }
    } catch { toast.error("Error saving status"); }
    finally { setSavingStatus(false); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "badge-warning",
      intake_complete: "badge-gold",
      eligibility_complete: "badge-gold",
      application_complete: "badge-gold",
      program_selected: "badge-gold",
      submitted: "badge-gold",
      under_review: "badge-gold",
      approved: "badge-success",
      rejected: "badge-error",
      waitlisted: "bg-purple-100 text-purple-800",
      transferred: "bg-slate-100 text-slate-600",
      enrolled: "badge-gold",
      in_progress: "badge-warning",
      completed: "badge-success"
    };
    return styles[status] || "badge-gold";
  };

  const getEnrollmentProgress = (enrollment) => {
    if (!enrollment) return 0;
    const step = enrollment.current_step || 1;
    if (["submitted", "approved", "rejected", "waitlisted", "transferred", "application_complete"].includes(enrollment.status)) return 100;
    return Math.round(((step - 1) / 4) * 100);
  };

  const getEnrollmentStepText = (enrollment) => {
    if (!enrollment) return "Not started";
    const step = enrollment.current_step || 1;
    const statusMap = {
      'draft': `Step ${step} of 4 - In Progress`,
      'intake_complete': 'Step 2 of 4 - Eligibility',
      'eligibility_complete': 'Step 3 of 4 - Program Application',
      'application_complete': 'Step 4 of 4 - Choose Start Date',
      'program_selected': 'Step 4 of 4 - Application Questions',
      'submitted': 'Submitted - Awaiting Review',
      'under_review': 'Under Review',
      'approved': 'Approved!',
      'rejected': 'Not Approved',
      'waitlisted': 'Waitlisted',
      'transferred': 'Transferred to Another Cohort'
    };
    return statusMap[enrollment.status] || enrollment.status;
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await fetch(`${API}/talent/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: profileForm.name,
          email: profileForm.email,
          education: profileForm.education,
          work_experience: profileForm.work_experience,
          skills: profileForm.skills,
          city: profileForm.city,
          state: profileForm.state,
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setTalentProfile(updated);
        setProfileForm(updated);
        setEditingProfile(false);
        toast.success("Profile updated!");
      } else {
        toast.error("Failed to save profile");
      }
    } catch { toast.error("Error saving profile"); }
    finally { setSavingProfile(false); }
  };

  if (loading) {
    return (
      <PageLayout title="Dashboard">
        <div className="flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title={`Welcome back, ${user?.name?.split(' ')[0] || 'Learner'}!`}
      subtitle="Track your progress and continue your learning journey."
    >
      <div className="space-y-8" data-testid="participant-dashboard">
        <MfaEncourageBanner />
        <SignDocsBanner />
        <LiveClassWidget endpoint="/me/live-class" variant="dashboard" />
        <StrengthsDashboardCard />
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="card-surface stats-card" data-testid="stat-courses">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Enrolled Courses</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.enrolled_courses || 0}</p>
                </div>
                <div className="w-12 h-12 bg-brand-gold/20 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-brand-navy" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-surface stats-card" data-testid="stat-completed">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Completed Courses</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.completed_courses || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#10B981]/20 rounded-lg flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-[#10B981]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-surface stats-card" data-testid="stat-mentorships">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Active Mentorships</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.active_mentorships || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#1E3A8A]/40 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#3B82F6]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Working Style Quiz CTA */}
        <Link to="/working-style" className="block" data-testid="working-style-cta-link">
          <Card className="border-2 border-[#FFD507]/30 bg-gradient-to-br from-[#FFD507]/5 to-[#1E3A8A]/5 hover:border-[#FFD507]/70 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer" data-testid="working-style-cta">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex items-start gap-4 flex-1 min-w-[260px]">
                  <div className="w-12 h-12 rounded-lg bg-[#FFD507]/20 flex items-center justify-center shrink-0">
                    <Rocket className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-bold text-slate-900">Tell managers how you work best</h3>
                    <p className="text-sm text-slate-600 mt-1 max-w-xl">
                      Take the short <span className="font-semibold">Working With Me</span> quiz — a neurodivergent-friendly way to share your communication style, sensory needs and strengths with current or future managers. Private by default.
                    </p>
                  </div>
                </div>
                <span className="btn-primary inline-flex items-center gap-2 shrink-0 pointer-events-none" data-testid="working-style-cta-btn">
                  Start the Quiz <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Enrollment Status */}
        <Card className="card-surface" data-testid="enrollment-status-card">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-brand-navy" />
              Academy Enrollment
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentEnrollment ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">
                      {currentEnrollment.program_selection?.program_name || currentEnrollment.intake?.program_of_interest?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Application'}
                    </p>
                    <p className="text-sm text-slate-500">{getEnrollmentStepText(currentEnrollment)}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(currentEnrollment.status)}`}>
                    {currentEnrollment.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Progress</span>
                    <span className="text-brand-navy font-medium">{getEnrollmentProgress(currentEnrollment)}%</span>
                  </div>
                  <Progress value={getEnrollmentProgress(currentEnrollment)} className="h-2 bg-[#1E3A8A]/30" />
                </div>
                {currentEnrollment.status !== 'submitted' && currentEnrollment.status !== 'approved' && currentEnrollment.status !== 'rejected' && currentEnrollment.status !== 'waitlisted' && currentEnrollment.status !== 'transferred' && (
                  <Link 
                    to="/apply" 
                    className="btn-primary inline-flex items-center gap-2 mt-2"
                    data-testid="continue-enrollment-btn"
                  >
                    Continue Enrollment <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {currentEnrollment.status === 'submitted' && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">Your application is being reviewed. We'll notify you soon!</span>
                    </div>
                    {currentEnrollment.cohort_full_at_submission && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800 font-medium">This program is currently full. We will be in touch as we are able to add additional cohorts.</p>
                      </div>
                    )}
                  </div>
                )}
                {currentEnrollment.status === 'approved' && (
                  <div className="flex items-center gap-2 text-[#10B981] mt-2">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Congratulations! Your application has been approved.</span>
                    {currentEnrollment.review_notes && (
                      <p className="text-xs text-slate-500 mt-1">Note: {currentEnrollment.review_notes}</p>
                    )}
                  </div>
                )}
                {currentEnrollment.status === 'rejected' && (
                  <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-100">
                    <p className="text-sm text-red-700 font-medium">Your application was not approved at this time.</p>
                    {currentEnrollment.review_notes && (
                      <p className="text-xs text-red-600 mt-1">Feedback: {currentEnrollment.review_notes}</p>
                    )}
                  </div>
                )}
                {currentEnrollment.status === 'waitlisted' && (
                  <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <p className="text-sm text-purple-700 font-medium">You've been placed on the waitlist for this cohort.</p>
                    {currentEnrollment.review_notes && (
                      <p className="text-xs text-purple-600 mt-1">Note: {currentEnrollment.review_notes}</p>
                    )}
                    <p className="text-xs text-purple-500 mt-1">We'll reach out if a spot opens up.</p>
                  </div>
                )}
                {currentEnrollment.status === 'transferred' && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-700 font-medium">You've been transferred to a different cohort.</p>
                    {currentEnrollment.review_notes && (
                      <p className="text-xs text-blue-600 mt-1">Note: {currentEnrollment.review_notes}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Start your journey with Aspiritech Academy</p>
                <Link to="/apply" className="btn-primary inline-flex items-center gap-2" data-testid="start-enrollment-btn">
                  Begin Enrollment <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Post-Training Status Card */}
        <Card className="card-surface" data-testid="post-training-status-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Award className="w-5 h-5 text-brand-navy" />
              Post-Training Status
            </CardTitle>
            <div className="flex items-center gap-2">
              {user?.is_certified && (
                <Badge className="bg-green-100 text-green-700 border border-green-200">
                  <CheckCircle className="w-3 h-3 mr-1" /> Certified
                </Badge>
              )}
              {user?.is_alumni && (
                <Badge className="bg-brand-gold/10 text-brand-navy">Alumni</Badge>
              )}
              {!editingStatus ? (
                <Button size="sm" variant="ghost" onClick={() => setEditingStatus(true)} className="text-slate-400 hover:text-slate-900" data-testid="edit-status-btn">
                  <Pencil className="w-4 h-4" />
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => {
                    setEditingStatus(false);
                    setStatusForm({
                      post_training_status: user?.post_training_status || "",
                      employer_name: user?.post_training_employer || "",
                      start_date: user?.post_training_start_date || "",
                      notes: user?.post_training_notes || "",
                      industry: user?.industry || ""
                    });
                  }} className="text-slate-400 hover:text-slate-900">
                    <X className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={savePostTrainingStatus} disabled={savingStatus} className="btn-primary h-8 px-3" data-testid="save-status-btn">
                    <Save className="w-3.5 h-3.5 mr-1" /> {savingStatus ? "Saving..." : "Save"}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editingStatus ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm">Current Status *</Label>
                  <Select value={statusForm.post_training_status} onValueChange={(val) => setStatusForm(f => ({ ...f, post_training_status: val }))}>
                    <SelectTrigger className="input-base" data-testid="select-post-training-status">
                      <SelectValue placeholder="Select your current status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {POST_TRAINING_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Employer / Organization</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={statusForm.employer_name}
                        onChange={(e) => setStatusForm(f => ({ ...f, employer_name: e.target.value }))}
                        className="input-base pl-10"
                        placeholder="e.g., Aspiritech, Google"
                        data-testid="input-employer-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Start Date</Label>
                    <div className="relative">
                      <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="date"
                        value={statusForm.start_date}
                        onChange={(e) => setStatusForm(f => ({ ...f, start_date: e.target.value }))}
                        className="input-base pl-10"
                        data-testid="input-start-date"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm">Industry</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={statusForm.industry}
                      onChange={(e) => setStatusForm(f => ({ ...f, industry: e.target.value }))}
                      className="input-base pl-10"
                      placeholder="e.g., Cybersecurity, Quality Assurance, Healthcare IT"
                      data-testid="input-industry"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm">Notes (optional)</Label>
                  <Textarea
                    value={statusForm.notes}
                    onChange={(e) => setStatusForm(f => ({ ...f, notes: e.target.value }))}
                    className="input-base min-h-[80px]"
                    placeholder="Any additional details about your current role or situation..."
                    data-testid="input-status-notes"
                  />
                </div>
              </div>
            ) : (
              <div>
                {user?.post_training_status ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-brand-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Award className="w-4 h-4 text-brand-navy" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wider">Status</p>
                          <p className="text-sm font-medium text-slate-900">{user.post_training_status}</p>
                        </div>
                      </div>
                      {user.post_training_employer && (
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-brand-navy/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Building2 className="w-4 h-4 text-brand-navy" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Employer</p>
                            <p className="text-sm font-medium text-slate-900">{user.post_training_employer}</p>
                          </div>
                        </div>
                      )}
                      {user.post_training_start_date && (
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-brand-navy/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CalendarDays className="w-4 h-4 text-brand-navy" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Start Date</p>
                            <p className="text-sm font-medium text-slate-900">{new Date(user.post_training_start_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    {user.post_training_notes && (
                      <div className="pt-2 border-t border-slate-100">
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Notes</p>
                        <p className="text-sm text-slate-700 whitespace-pre-line">{user.post_training_notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm mb-3">Update your post-training work experience status</p>
                    <Button onClick={() => setEditingStatus(true)} className="btn-primary" data-testid="set-status-btn">
                      Set Your Status
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Talent Profile */}
        {talentProfile && (
          <Card className="card-surface" data-testid="talent-profile-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-brand-navy" />
                My Talent Profile
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge className={talentProfile.is_visible ? "bg-[#10B981]/10 text-[#059669]" : "bg-slate-100 text-slate-500"}>
                  {talentProfile.is_visible ? "Visible" : "Hidden"}
                </Badge>
                {talentProfile.enrollment_status && (
                  <Badge className="bg-brand-gold/10 text-brand-navy capitalize">
                    {talentProfile.enrollment_status.replace("_", " ")}
                  </Badge>
                )}
                {!editingProfile ? (
                  <Button size="sm" variant="ghost" onClick={() => setEditingProfile(true)} className="text-slate-400 hover:text-slate-900" data-testid="edit-profile-btn">
                    <Pencil className="w-4 h-4" />
                  </Button>
                ) : (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditingProfile(false); setProfileForm(talentProfile); }} className="text-slate-400 hover:text-slate-900">
                      <X className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={saveProfile} disabled={savingProfile} className="btn-primary h-8 px-3" data-testid="save-profile-btn">
                      <Save className="w-3.5 h-3.5 mr-1" /> {savingProfile ? "Saving..." : "Save"}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingProfile ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-slate-600 text-sm">Name</Label>
                      <Input value={profileForm.name || ""} onChange={(e) => setProfileForm(p => ({ ...p, name: e.target.value }))} className="input-base" data-testid="profile-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-600 text-sm">Email</Label>
                      <Input value={profileForm.email || ""} onChange={(e) => setProfileForm(p => ({ ...p, email: e.target.value }))} className="input-base" data-testid="profile-email-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-600 text-sm">City</Label>
                      <Input value={profileForm.city || ""} onChange={(e) => setProfileForm(p => ({ ...p, city: e.target.value }))} className="input-base" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-600 text-sm">State</Label>
                      <Input value={profileForm.state || ""} onChange={(e) => setProfileForm(p => ({ ...p, state: e.target.value }))} className="input-base" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-600 text-sm">Education</Label>
                    <Input value={profileForm.education || ""} onChange={(e) => setProfileForm(p => ({ ...p, education: e.target.value }))} className="input-base" data-testid="profile-education-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-600 text-sm">Work Experience</Label>
                    <Textarea value={profileForm.work_experience || ""} onChange={(e) => setProfileForm(p => ({ ...p, work_experience: e.target.value }))} className="input-base min-h-[80px]" data-testid="profile-experience-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-600 text-sm">Skills (comma-separated)</Label>
                    <Input
                      value={Array.isArray(profileForm.skills) ? profileForm.skills.join(", ") : ""}
                      onChange={(e) => setProfileForm(p => ({ ...p, skills: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))}
                      className="input-base"
                      placeholder="e.g., QA Testing, Python, Accessibility"
                      data-testid="profile-skills-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-brand-navy/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <GraduationCap className="w-4 h-4 text-brand-navy" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Name</p>
                        <p className="text-sm font-medium text-slate-900">{talentProfile.name || "—"}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-brand-navy/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Mail className="w-4 h-4 text-brand-navy" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Email</p>
                        <p className="text-sm font-medium text-slate-900">{talentProfile.email || "—"}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-brand-navy/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <BookOpen className="w-4 h-4 text-brand-navy" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Education</p>
                        <p className="text-sm font-medium text-slate-900">{talentProfile.education || "—"}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-brand-navy/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="w-4 h-4 text-brand-navy" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Location</p>
                        <p className="text-sm font-medium text-slate-900">{[talentProfile.city, talentProfile.state].filter(Boolean).join(", ") || "—"}</p>
                      </div>
                    </div>
                  </div>
                  {talentProfile.work_experience && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Work Experience</p>
                      <p className="text-sm text-slate-700 whitespace-pre-line">{talentProfile.work_experience}</p>
                    </div>
                  )}
                  {talentProfile.skills?.length > 0 && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Skills</p>
                      <div className="flex flex-wrap gap-1.5">
                        {talentProfile.skills.map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs bg-brand-gold/10 text-brand-navy rounded-full">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {talentProfile.resume_url && (
                    <div className="pt-2 border-t border-slate-100 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-brand-navy" />
                      <span className="text-sm text-brand-navy font-medium">Resume uploaded</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Current Courses */}
        <Card className="card-surface" data-testid="current-courses-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-brand-navy" />
              Your Courses
            </CardTitle>
            <Link to="/courses" className="text-brand-navy text-sm hover:underline flex items-center gap-1">
              Browse All <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {enrollments.length > 0 ? (
              <div className="space-y-4">
                {enrollments.slice(0, 3).map((enrollment) => (
                  <div key={enrollment.enrollment_id} className="p-4 bg-surface-muted rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-slate-900">{enrollment.course?.title || 'Course'}</h4>
                        <p className="text-sm text-slate-500">{enrollment.course?.instructor_name || 'Instructor'}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(enrollment.status)}`}>
                        {enrollment.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Progress</span>
                        <span className="text-brand-navy font-medium">{enrollment.progress}%</span>
                      </div>
                      <Progress value={enrollment.progress} className="h-2 bg-[#1E3A8A]/30" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">You haven't enrolled in any courses yet.</p>
                <Link to="/courses" className="btn-primary inline-flex items-center gap-2" data-testid="browse-courses-btn">
                  Browse Courses <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/apply" className="card-surface card-hover p-6 block" data-testid="quick-action-apply">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-brand-gold/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-brand-navy" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Enrollment</p>
                <p className="text-sm text-slate-500">Continue application</p>
              </div>
            </div>
          </Link>

          <Link to="/mentorship" className="card-surface card-hover p-6 block" data-testid="quick-action-mentorship">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#1E3A8A]/40 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Find a Mentor</p>
                <p className="text-sm text-slate-500">Connect with experts</p>
              </div>
            </div>
          </Link>

          <Link to="/talent" className="card-surface card-hover p-6 block" data-testid="quick-action-talent">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#10B981]/20 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-[#10B981]" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Talent Database</p>
                <p className="text-sm text-slate-500">Showcase your skills</p>
              </div>
            </div>
          </Link>

          <Link to="/alumni" className="card-surface card-hover p-6 block" data-testid="quick-action-alumni">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#F59E0B]/20 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Alumni Network</p>
                <p className="text-sm text-slate-500">Join the community</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </PageLayout>
  );
};

export default Dashboard;
